import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Crown, Eye, Globe2, Swords } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { FORMATS } from "@/components/companion/Lobby";

const MODES = [
  ["planechase", Globe2, "Planechase", "Roll the planar die, planeswalk across official and custom planes, and handle phenomena."],
  ["archenemy", Crown, "Archenemy", "Set schemes in motion, track ongoing schemes and recycle the scheme deck."],
  ["vanguard", Swords, "Vanguard", "Deal Vanguard choices or use your deck's avatar, with hand and life modifiers."],
];

function Create({ deckId }) {
  const nav = useNavigate();
  const { data: deck } = useQuery({ queryKey: ["deck", deckId], enabled: !!deckId, queryFn: () => api.get(`/decks/${deckId}`).then((r) => r.data) });
  const [f, setF] = useState({ name: "", base_format: "commander", team_mode: "ffa", modes: { planechase: true, archenemy: false, vanguard: false } });
  useEffect(() => {
    if (!deck) return;
    const z = new Set(deck.entries.map((e) => e.zone));
    setF((x) => ({ ...x, name: `${deck.name} night`, modes: { planechase: z.has("planar"), archenemy: z.has("scheme"), vanguard: z.has("vanguard") }, team_mode: z.has("scheme") ? "archenemy" : "ffa" }));
  }, [deck]);
  const submit = async (e) => {
    e.preventDefault();
    try {
      const s = (await api.post("/companion/sessions", f)).data;
      if (deckId) await api.post(`/companion/sessions/${s.code}/seats/${s.seats[0].seat_id}/deck`, { deck_id: deckId }).catch((err) => toast.error(formatApiError(err)));
      nav(`/companion/s/${s.code}`);
    } catch (err) { toast.error(formatApiError(err)); }
  };
  return (
    <form className="panel space-y-4 p-6" onSubmit={submit} data-testid="create-session-form">
      <p className="eyebrow">Host a session{deck ? ` from ${deck.name}` : ""}</p>
      <input className="field" placeholder="Session name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="create-session-name" />
      <div className="grid gap-3 sm:grid-cols-2">
        <select className="field" value={f.base_format} onChange={(e) => setF({ ...f, base_format: e.target.value })} data-testid="create-session-format">{Object.entries(FORMATS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}</select>
        <select className="field" value={f.team_mode} onChange={(e) => setF({ ...f, team_mode: e.target.value })} data-testid="create-session-teams"><option value="ffa">Free-for-all</option><option value="teams">Teams</option><option value="archenemy">Archenemy vs. heroes</option></select>
      </div>
      <div className="flex flex-wrap gap-5">{MODES.map(([m, , label]) => <label key={m} className="flex items-center gap-2 text-sm text-slate-200"><input type="checkbox" checked={f.modes[m]} onChange={(e) => setF({ ...f, modes: { ...f.modes, [m]: e.target.checked } })} data-testid={`create-mode-${m}`} />{label}</label>)}</div>
      <button className="btn-gold min-h-[52px] w-full" data-testid="create-session-button">Open a portal</button>
    </form>
  );
}

function Join() {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const [code, setCode] = useState(params.get("code") || "");
  const join = async (role) => {
    try { await api.post("/companion/join", { code, role }); nav(`/companion/s/${code.toUpperCase()}`); } catch (err) { toast.error(formatApiError(err)); }
  };
  return (
    <div className="panel space-y-4 p-6" data-testid="join-session-form">
      <p className="eyebrow">Join with a code</p>
      <input className="field text-center font-mono text-2xl uppercase tracking-[0.4em]" maxLength={8} placeholder="ABC123" value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} data-testid="join-code-input" />
      <div className="grid grid-cols-2 gap-3">
        <button className="btn-gold min-h-[52px]" disabled={code.length < 4} onClick={() => join("player")} data-testid="join-player-button">Join as player</button>
        <button className="btn-outline min-h-[52px]" disabled={code.length < 4} onClick={() => join("spectator")} data-testid="join-spectator-button"><Eye className="h-4 w-4" />Watch</button>
      </div>
    </div>
  );
}

export default function CompanionHub() {
  const { user } = useAuth();
  const [params] = useSearchParams();
  const { data } = useQuery({ queryKey: ["companion", "mine"], enabled: !!user, queryFn: () => api.get("/companion/sessions").then((r) => r.data.items) });
  return (
    <div className="container-x py-12" data-testid="companion-hub">
      <p className="eyebrow">The Planar Foundry</p>
      <h1 className="font-display text-4xl text-amber-100 sm:text-6xl">Multiverse Companion</h1>
      <p className="mt-3 max-w-2xl text-sm text-slate-400">A control console for paper Magic: run Planechase, Archenemy and Vanguard on any screen while you play with real cards. Mix official and custom cards from your library, alone or together.</p>
      <div className="mt-10 grid gap-4 md:grid-cols-3">{MODES.map(([m, Icon, label, text]) => (
        <div key={m} className="panel p-5" data-testid={`mode-tile-${m}`}><Icon className="h-8 w-8 text-aether-300" /><h2 className="mt-3 font-heading text-xl text-amber-100">{label}</h2><p className="mt-1 text-sm text-slate-400">{text}</p></div>))}</div>
      {!user ? <p className="panel mt-10 p-6 text-slate-300" data-testid="companion-signin">Everyone in a session signs in, spectators included. <Link to="/login" className="text-aether-300">Sign in</Link> to host or join.</p> : (
        <div className="mt-10 grid gap-6 lg:grid-cols-2"><Create deckId={params.get("deck")} /><Join /></div>)}
      {data?.length > 0 && <section className="mt-10" data-testid="my-sessions"><p className="eyebrow mb-3">Your open sessions</p>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">{data.map((s) => <Link key={s.code} to={`/companion/s/${s.code}`} className="panel block p-4 hover:border-amber-500/60" data-testid={`my-session-${s.code}`}>
          <p className="font-heading text-amber-100">{s.name}</p><p className="font-mono text-xs text-slate-500">{s.code} · {s.status} · {Object.keys(s.modes).filter((m) => s.modes[m]).join(" + ")}</p></Link>)}</div></section>}
    </div>
  );
}
